import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientInvoicesAPI, clientsAPI, timesheetsAPI, jobsAPI, resourcesAPI, customEntriesAPI, usersAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { toast } from 'sonner';
import { Plus, Loader2, Check, X, Filter, Eye, Clock, TrendingUp, TrendingDown, Minus, Download, FileText, Pencil, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { className: 'badge-draft', label: 'Draft' },
    sent: { className: 'badge-pending', label: 'Sent' },
    paid: { className: 'badge-approved', label: 'Paid' },
    overdue: { className: 'badge-rejected', label: 'Overdue' },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
};

const DifferenceDisplay = ({ estimated, actual }) => {
  if (!estimated || estimated === 0) {
    return <span className="text-slate-400 text-xs">-</span>;
  }
  const diff = actual - estimated;
  if (Math.abs(diff) < 0.01) {
    return (
      <span className="flex items-center gap-1 text-slate-500 text-xs">
        <Minus className="w-3 h-3" /> $0.00
      </span>
    );
  }
  if (diff > 0) {
    return (
      <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
        <TrendingUp className="w-3 h-3" /> +${diff.toFixed(2)}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
      <TrendingDown className="w-3 h-3" /> -${Math.abs(diff).toFixed(2)}
    </span>
  );
};

// Invoice View Modal - shows linked timesheets and custom entries
const InvoiceViewModal = ({ isOpen, onClose, invoice, timesheets, customEntries, resources, jobs, clientsMap }) => {
  if (!isOpen || !invoice) return null;

  const linkedTimesheets = timesheets.filter(ts => 
    invoice.timesheet_ids && invoice.timesheet_ids.includes(ts.id)
  );
  
  const linkedCustomEntries = (customEntries || []).filter(ce => 
    invoice.custom_entry_ids && invoice.custom_entry_ids.includes(ce.id)
  );

  const getResourceName = (userId) => {
    const resource = resources.find(r => r.user_id === userId);
    return resource?.name || 'Unknown';
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.job_number}` : '-';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Client Invoice</h2>
            <p className="text-sm text-slate-500 font-mono">{invoice.invoice_number}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase">Client</p>
              <p className="text-sm font-medium text-slate-900">
                {clientsMap[invoice.client_id]?.name || '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Date</p>
              <p className="text-sm font-medium text-slate-900">{formatDate(invoice.invoice_date)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Total</p>
              <p className="text-sm font-bold text-slate-900">${invoice.total?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <StatusBadge status={invoice.status} />
            </div>
          </div>

          {/* Linked Timesheets */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Linked Timesheets ({linkedTimesheets.length})
            </h3>
            
            {linkedTimesheets.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                No timesheets linked to this invoice
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y">
                {linkedTimesheets.map(ts => (
                  <Link
                    key={ts.id}
                    to={`/timesheets/${ts.id}`}
                    className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ts.period_start, ts.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ts.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {getResourceName(ts.user_id)} • 
                        {ts.total_days > 0 && ` ${ts.total_days} days`}
                        {ts.total_hours > 0 && ` ${ts.total_hours} hrs`}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-slate-400" />
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {/* Linked Custom Entries */}
          {linkedCustomEntries.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Linked Custom Entries ({linkedCustomEntries.length})
              </h3>
              
              <div className="border border-slate-200 rounded-lg divide-y">
                {linkedCustomEntries.map(ce => (
                  <div
                    key={ce.id}
                    className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ce.period_start, ce.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                          Custom
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ce.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {getResourceName(ce.user_id)} • Value: ${ce.total_value?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t bg-slate-50">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Invoice Modal - for editing draft invoices (add/remove linked items)
const EditInvoiceModal = ({ isOpen, onClose, onSave, invoice, timesheets, customEntries, resources, jobs, allClientInvoices, saving }) => {
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    subtotal: '',
    gst: '',
    total: '',
  });
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [selectedCustomEntries, setSelectedCustomEntries] = useState([]);
  const [estimatedBilling, setEstimatedBilling] = useState(0);
  const [users, setUsers] = useState({});

  // Initialize form when invoice changes
  useEffect(() => {
    if (isOpen && invoice) {
      setFormData({
        invoice_number: invoice.invoice_number || '',
        invoice_date: invoice.invoice_date || '',
        due_date: invoice.due_date || '',
        subtotal: invoice.subtotal?.toString() || '',
        gst: invoice.gst?.toString() || '',
        total: invoice.total?.toString() || '',
      });
      setSelectedTimesheets(invoice.timesheet_ids || []);
      setSelectedCustomEntries(invoice.custom_entry_ids || []);
      
      // Load users
      usersAPI.getAll().then(res => {
        const usersMap = {};
        res.data.forEach(u => { usersMap[u.id] = u; });
        setUsers(usersMap);
      });
    }
  }, [isOpen, invoice]);

  // Calculate estimated billing when selections change
  useEffect(() => {
    const calculateEstimate = async () => {
      if (selectedTimesheets.length === 0 && selectedCustomEntries.length === 0) {
        setEstimatedBilling(0);
        return;
      }
      try {
        const res = await timesheetsAPI.estimateBilling(selectedTimesheets, selectedCustomEntries);
        setEstimatedBilling(res.data.total_estimated_billing);
      } catch (error) {
        console.error('Failed to calculate estimate:', error);
      }
    };
    calculateEstimate();
  }, [selectedTimesheets, selectedCustomEntries]);

  // Get available timesheets (approved ones not linked to other invoices)
  const getAvailableTimesheets = () => {
    const linkedTsIds = new Set();
    allClientInvoices.forEach(inv => {
      if (inv.id !== invoice?.id) {
        (inv.timesheet_ids || []).forEach(tid => linkedTsIds.add(tid));
      }
    });
    
    return timesheets.filter(ts => 
      ts.status === 'approved' && 
      (!linkedTsIds.has(ts.id) || selectedTimesheets.includes(ts.id))
    );
  };

  // Get available custom entries (not linked to other invoices)
  const getAvailableCustomEntries = () => {
    const linkedCeIds = new Set();
    allClientInvoices.forEach(inv => {
      if (inv.id !== invoice?.id) {
        (inv.custom_entry_ids || []).forEach(cid => linkedCeIds.add(cid));
      }
    });
    
    return customEntries.filter(ce => 
      !linkedCeIds.has(ce.id) || selectedCustomEntries.includes(ce.id)
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateGST = () => {
    const subtotal = parseFloat(formData.subtotal) || 0;
    const gst = subtotal * 0.1;
    const total = subtotal + gst;
    setFormData(prev => ({
      ...prev,
      gst: gst.toFixed(2),
      total: total.toFixed(2)
    }));
  };

  const handleTimesheetToggle = (timesheetId) => {
    setSelectedTimesheets(prev => {
      if (prev.includes(timesheetId)) {
        return prev.filter(id => id !== timesheetId);
      }
      return [...prev, timesheetId];
    });
  };

  const handleCustomEntryToggle = (entryId) => {
    setSelectedCustomEntries(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId);
      }
      return [...prev, entryId];
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(invoice.id, {
      invoice_number: formData.invoice_number,
      invoice_date: formData.invoice_date,
      due_date: formData.due_date,
      subtotal: parseFloat(formData.subtotal) || 0,
      gst: parseFloat(formData.gst) || 0,
      total: parseFloat(formData.total) || 0,
      timesheet_ids: selectedTimesheets,
      custom_entry_ids: selectedCustomEntries,
    });
  };

  const getResourceName = (userId) => {
    const resource = resources.find(r => r.user_id === userId);
    return resource?.name || users[userId]?.name || 'Unknown';
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.job_number}` : '-';
  };

  const availableTs = getAvailableTimesheets();
  const availableCe = getAvailableCustomEntries();

  if (!isOpen || !invoice) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Edit Invoice</h2>
            <p className="text-sm text-slate-500">Modify linked timesheets and custom entries</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Invoice Details Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
              <input
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              />
            </div>
          </div>

          {/* Timesheet Selection */}
          <div className="border border-slate-200 rounded-lg p-4">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4" />
              Linked Timesheets ({selectedTimesheets.length} selected)
            </label>
            
            {availableTs.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No approved timesheets available</p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                {availableTs.map(ts => (
                  <label
                    key={ts.id}
                    className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                      selectedTimesheets.includes(ts.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTimesheets.includes(ts.id)}
                      onChange={() => handleTimesheetToggle(ts.id)}
                      className="w-4 h-4 text-[#194eb2] rounded border-slate-300 focus:ring-[#194eb2]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ts.period_start, ts.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ts.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {getResourceName(ts.user_id)} • 
                        {ts.total_days > 0 && ` ${ts.total_days} days`}
                        {ts.total_hours > 0 && ` ${ts.total_hours} hrs`}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Custom Entries Selection */}
          <div className="border border-slate-200 rounded-lg p-4">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4" />
              Linked Custom Entries ({selectedCustomEntries.length} selected)
            </label>
            
            {availableCe.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No custom entries available</p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                {availableCe.map(ce => (
                  <label
                    key={ce.id}
                    className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                      selectedCustomEntries.includes(ce.id) ? 'bg-amber-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomEntries.includes(ce.id)}
                      onChange={() => handleCustomEntryToggle(ce.id)}
                      className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ce.period_start, ce.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                          Custom
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ce.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {getResourceName(ce.user_id)} • Value: ${ce.total_value?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Estimated Billing Display */}
          {(selectedTimesheets.length > 0 || selectedCustomEntries.length > 0) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">
                  Estimated Billing
                  {selectedTimesheets.length > 0 && ` (${selectedTimesheets.length} timesheet${selectedTimesheets.length > 1 ? 's' : ''})`}
                  {selectedCustomEntries.length > 0 && ` + ${selectedCustomEntries.length} custom entr${selectedCustomEntries.length > 1 ? 'ies' : 'y'}`}
                </span>
                <span className="text-lg font-bold text-blue-800">
                  ${estimatedBilling.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Amount Fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subtotal (excl. GST)</label>
              <input
                type="number"
                name="subtotal"
                value={formData.subtotal}
                onChange={handleChange}
                onBlur={calculateGST}
                required
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST (10%)</label>
              <input
                type="number"
                name="gst"
                value={formData.gst}
                readOnly
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total</label>
              <input
                type="number"
                name="total"
                value={formData.total}
                readOnly
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 font-mono font-semibold"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              data-testid="save-edit-invoice-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client Invoice Modal with Timesheet Selection
const ClientInvoiceModal = ({ isOpen, onClose, onSave, clients, jobs, resources, allClientInvoices, saving }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    subtotal: '',
    gst: '',
    total: '',
  });
  const [availableTimesheets, setAvailableTimesheets] = useState([]);
  const [availableCustomEntries, setAvailableCustomEntries] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [selectedCustomEntries, setSelectedCustomEntries] = useState([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [estimatedBilling, setEstimatedBilling] = useState(0);
  const [filters, setFilters] = useState({
    periodStart: '',
    periodEnd: '',
    resourceId: '',
    jobId: '',
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        client_id: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        subtotal: '',
        gst: '',
        total: '',
      });
      setSelectedTimesheets([]);
      setSelectedCustomEntries([]);
      setEstimatedBilling(0);
      setFilters({ periodStart: '', periodEnd: '', resourceId: '', jobId: '' });
      loadAllApprovedTimesheets();
    }
  }, [isOpen]);

  // Calculate estimated billing when selected timesheets/custom entries change
  useEffect(() => {
    const calculateEstimate = async () => {
      if (selectedTimesheets.length === 0 && selectedCustomEntries.length === 0) {
        setEstimatedBilling(0);
        return;
      }
      try {
        const res = await timesheetsAPI.estimateBilling(selectedTimesheets, selectedCustomEntries);
        setEstimatedBilling(res.data.total_estimated_billing);
      } catch (error) {
        console.error('Failed to calculate estimate:', error);
      }
    };
    calculateEstimate();
  }, [selectedTimesheets, selectedCustomEntries]);

  const loadAllApprovedTimesheets = async () => {
    setLoadingTimesheets(true);
    try {
      const [tsRes, ceRes, usersRes] = await Promise.all([
        timesheetsAPI.getAll('approved'),
        customEntriesAPI.getAll(),
        usersAPI.getAll()
      ]);
      
      const approvedTimesheets = tsRes.data.filter(ts => ts.status === 'approved');
      const customEntries = ceRes.data;
      
      // Create users map
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);
      
      // Get IDs already linked to other client invoices
      const linkedTsIds = new Set();
      const linkedCeIds = new Set();
      allClientInvoices.forEach(inv => {
        (inv.timesheet_ids || []).forEach(tid => linkedTsIds.add(tid));
        (inv.custom_entry_ids || []).forEach(cid => linkedCeIds.add(cid));
      });
      
      // Filter out already linked items
      const availableTs = approvedTimesheets.filter(ts => !linkedTsIds.has(ts.id));
      const availableCe = customEntries.filter(ce => !linkedCeIds.has(ce.id));
      
      setAvailableTimesheets(availableTs);
      setAvailableCustomEntries(availableCe);
    } catch (error) {
      console.error('Failed to load timesheets:', error);
      setAvailableTimesheets([]);
      setAvailableCustomEntries([]);
    } finally {
      setLoadingTimesheets(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // When client changes, clear selected timesheets and custom entries
    // since they may not be valid for the new client
    if (name === 'client_id') {
      setSelectedTimesheets([]);
      setSelectedCustomEntries([]);
    }
  };

  const calculateGST = () => {
    const subtotal = parseFloat(formData.subtotal) || 0;
    const gst = subtotal * 0.1;
    const total = subtotal + gst;
    setFormData(prev => ({
      ...prev,
      gst: gst.toFixed(2),
      total: total.toFixed(2)
    }));
  };

  const handleTimesheetToggle = (timesheetId) => {
    setSelectedTimesheets(prev => {
      if (prev.includes(timesheetId)) {
        return prev.filter(id => id !== timesheetId);
      } else {
        return [...prev, timesheetId];
      }
    });
  };

  const handleCustomEntryToggle = (entryId) => {
    setSelectedCustomEntries(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId);
      } else {
        return [...prev, entryId];
      }
    });
  };

  const clearFilters = () => {
    setFilters({ periodStart: '', periodEnd: '', resourceId: '', jobId: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      subtotal: parseFloat(formData.subtotal) || 0,
      gst: parseFloat(formData.gst) || 0,
      total: parseFloat(formData.total) || 0,
      timesheet_ids: selectedTimesheets,
      custom_entry_ids: selectedCustomEntries,
      lines: [],
    });
  };

  // Filter timesheets - include client filtering based on job's client_id
  const filteredTimesheets = availableTimesheets.filter(ts => {
    // Filter by selected client - only show timesheets for jobs belonging to this client
    if (formData.client_id) {
      const job = jobs.find(j => j.id === ts.job_id);
      if (!job || job.client_id !== formData.client_id) return false;
    }
    if (filters.periodStart && ts.period_end < filters.periodStart) return false;
    if (filters.periodEnd && ts.period_start > filters.periodEnd) return false;
    if (filters.resourceId && ts.user_id !== filters.resourceId) return false;
    if (filters.jobId && ts.job_id !== filters.jobId) return false;
    return true;
  });
  
  // Filter custom entries - include client filtering based on job's client_id
  const filteredCustomEntries = availableCustomEntries.filter(ce => {
    // Filter by selected client - only show custom entries for jobs belonging to this client
    if (formData.client_id) {
      const job = jobs.find(j => j.id === ce.job_id);
      if (!job || job.client_id !== formData.client_id) return false;
    }
    if (filters.periodStart && ce.period_end < filters.periodStart) return false;
    if (filters.periodEnd && ce.period_start > filters.periodEnd) return false;
    if (filters.resourceId && ce.user_id !== filters.resourceId) return false;
    if (filters.jobId && ce.job_id !== filters.jobId) return false;
    return true;
  });

  const getResourceName = (userId) => {
    const resource = resources.find(r => r.user_id === userId);
    return resource?.name || users[userId]?.name || 'Unknown';
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.job_number}` : '-';
  };

  const hasActiveFilters = filters.periodStart || filters.periodEnd || filters.resourceId || filters.jobId;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-slate-900">Add Client Invoice</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Client Select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              data-testid="client-select"
            >
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Invoice Details Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number *</label>
              <input
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                required
                placeholder="INV-001"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent font-mono"
                data-testid="invoice-number-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date *</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              />
            </div>
          </div>

          {/* Timesheet Selection Section */}
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Link Timesheets (Optional)
              </label>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700">
                  Clear Filters
                </button>
              )}
            </div>
            
            {/* Filters Row */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">From</label>
                <input
                  type="date"
                  value={filters.periodStart}
                  onChange={(e) => setFilters(prev => ({ ...prev, periodStart: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2]"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">To</label>
                <input
                  type="date"
                  value={filters.periodEnd}
                  onChange={(e) => setFilters(prev => ({ ...prev, periodEnd: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2]"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Resource</label>
                <select
                  value={filters.resourceId}
                  onChange={(e) => setFilters(prev => ({ ...prev, resourceId: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2]"
                >
                  <option value="">All</option>
                  {[...resources].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(r => (
                    <option key={r.id} value={r.user_id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Project</label>
                <select
                  value={filters.jobId}
                  onChange={(e) => setFilters(prev => ({ ...prev, jobId: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2]"
                >
                  <option value="">All</option>
                  {jobs.filter(j => j.is_active).map(j => (
                    <option key={j.id} value={j.id}>{j.job_number} - {j.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timesheets List */}
            {loadingTimesheets ? (
              <div className="flex items-center gap-2 text-slate-500 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading timesheets...
              </div>
            ) : filteredTimesheets.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                {hasActiveFilters ? 'No timesheets match the filters' : 'No approved timesheets available'}
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {filteredTimesheets.map(ts => (
                  <label
                    key={ts.id}
                    className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                      selectedTimesheets.includes(ts.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTimesheets.includes(ts.id)}
                      onChange={() => handleTimesheetToggle(ts.id)}
                      className="w-4 h-4 text-[#194eb2] rounded border-slate-300 focus:ring-[#194eb2]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ts.period_start, ts.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ts.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {getResourceName(ts.user_id)} • 
                        {ts.total_days > 0 && ` ${ts.total_days} days`}
                        {ts.total_hours > 0 && ` ${ts.total_hours} hrs`}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            
          </div>

          {/* Custom Entries Selection Section */}
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Link Custom Entries (Optional)
              </label>
            </div>
            
            {/* Custom Entries List */}
            {loadingTimesheets ? (
              <div className="flex items-center gap-2 text-slate-500 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading custom entries...
              </div>
            ) : filteredCustomEntries.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                {hasActiveFilters ? 'No custom entries match the filters' : 'No custom entries available'}
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {filteredCustomEntries.map(ce => (
                  <label
                    key={ce.id}
                    className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                      selectedCustomEntries.includes(ce.id) ? 'bg-amber-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomEntries.includes(ce.id)}
                      onChange={() => handleCustomEntryToggle(ce.id)}
                      className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatDateRange(ce.period_start, ce.period_end)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                          Custom
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {getJobName(ce.job_id)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {getResourceName(ce.user_id)} • Value: ${ce.total_value?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          {/* Estimated Billing Display */}
          {(selectedTimesheets.length > 0 || selectedCustomEntries.length > 0) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">
                  Estimated Billing
                  {selectedTimesheets.length > 0 && ` (${selectedTimesheets.length} timesheet${selectedTimesheets.length > 1 ? 's' : ''})`}
                  {selectedCustomEntries.length > 0 && ` + ${selectedCustomEntries.length} custom entr${selectedCustomEntries.length > 1 ? 'ies' : 'y'}`}
                </span>
                <span className="text-lg font-bold text-blue-800">
                  ${estimatedBilling.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-1">Based on job charge-out rates and custom entry values</p>
            </div>
          )}

          {/* Amount Fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subtotal (excl. GST) *</label>
              <input
                type="number"
                name="subtotal"
                value={formData.subtotal}
                onChange={handleChange}
                onBlur={calculateGST}
                required
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent font-mono"
                data-testid="invoice-subtotal-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST (10%)</label>
              <input
                type="number"
                name="gst"
                value={formData.gst}
                readOnly
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total</label>
              <input
                type="number"
                name="total"
                value={formData.total}
                readOnly
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 font-mono font-semibold"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              data-testid="save-invoice-btn"
            >
              {saving ? 'Saving...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InvoicesPage = () => {
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [jobs, setJobs] = useState([]);
  const [resources, setResources] = useState([]);
  const [allTimesheets, setAllTimesheets] = useState([]);
  const [allCustomEntries, setAllCustomEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [estimatedBillingMap, setEstimatedBillingMap] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');

  // Generate period options (current month + last 6 months)
  const getPeriodOptions = () => {
    const options = [{ value: 'all', label: 'All Time' }];
    const today = new Date();
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      options.push({
        value: `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: `${monthName} ${year}`
      });
    }
    return options;
  };

  const periodOptions = getPeriodOptions();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientInvRes, clientsRes, jobsRes, resourcesRes, timesheetsRes, customEntriesRes] = await Promise.all([
        clientInvoicesAPI.getAll(),
        clientsAPI.getAll(),
        jobsAPI.getAll(),
        resourcesAPI.getAll(),
        timesheetsAPI.getAll(),
        customEntriesAPI.getAll()
      ]);
      
      setClientInvoices(clientInvRes.data);
      setClients(clientsRes.data);
      setJobs(jobsRes.data);
      // Sort resources alphabetically
      const sortedResources = resourcesRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setResources(sortedResources);
      setAllTimesheets(timesheetsRes.data);
      setAllCustomEntries(customEntriesRes.data);
      
      const cMap = {};
      clientsRes.data.forEach(c => { cMap[c.id] = c; });
      setClientsMap(cMap);

      // Calculate estimated billing for each invoice (including custom entries)
      const billingMap = {};
      for (const inv of clientInvRes.data) {
        const hasTimesheets = inv.timesheet_ids && inv.timesheet_ids.length > 0;
        const hasCustomEntries = inv.custom_entry_ids && inv.custom_entry_ids.length > 0;
        
        if (hasTimesheets || hasCustomEntries) {
          try {
            const estRes = await timesheetsAPI.estimateBilling(
              inv.timesheet_ids || [], 
              inv.custom_entry_ids || []
            );
            billingMap[inv.id] = estRes.data.total_estimated_billing;
          } catch (e) {
            billingMap[inv.id] = 0;
          }
        } else {
          billingMap[inv.id] = 0;
        }
      }
      setEstimatedBillingMap(billingMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const getFilteredInvoices = () => {
    let filtered = clientInvoices;
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }
    
    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(inv => inv.client_id === clientFilter);
    }
    
    // Period filter (by invoice date)
    if (periodFilter !== 'all') {
      filtered = filtered.filter(inv => {
        const invoiceDate = inv.invoice_date;
        return invoiceDate && invoiceDate.startsWith(periodFilter);
      });
    }
    
    return filtered;
  };

  const filteredInvoices = getFilteredInvoices();

  const handleViewInvoice = (invoice) => {
    setViewingInvoice(invoice);
    setShowViewModal(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setShowEditModal(true);
  };

  const handleSaveClientInvoice = async (data) => {
    setSaving(true);
    try {
      await clientInvoicesAPI.create(data);
      setShowClientModal(false);
      toast.success('Client invoice created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoice = async (invoiceId, data) => {
    setSaving(true);
    try {
      await clientInvoicesAPI.update(invoiceId, data);
      setShowEditModal(false);
      setEditingInvoice(null);
      toast.success('Invoice updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (invoiceId, newStatus) => {
    try {
      await clientInvoicesAPI.updateStatus(invoiceId, newStatus, newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}? This action cannot be undone.`)) {
      return;
    }
    try {
      await clientInvoicesAPI.delete(invoice.id);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Client Invoices</h1>
            <p className="mt-1 text-slate-500">Manage client invoices and track billing</p>
          </div>
          <button
            onClick={() => setShowClientModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-invoice-btn"
          >
            <Plus className="w-4 h-4" />
            Add Invoice
          </button>
        </div>

        {/* Filters */}
        <div className="card-base">
          <div className="card-body space-y-4">
            {/* Status Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    statusFilter === status
                      ? 'bg-[#194eb2] text-white'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`filter-${status}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/20 rounded">
                    {status === 'all' ? clientInvoices.length : clientInvoices.filter(inv => inv.status === status).length}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Client and Period Filters */}
            <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600">Client:</label>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="input-base w-56"
                  data-testid="client-filter"
                >
                  <option value="all">All Clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600">Period:</label>
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="input-base w-48"
                  data-testid="period-filter"
                >
                  {periodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {(clientFilter !== 'all' || periodFilter !== 'all') && (
                <button
                  onClick={() => {
                    setClientFilter('all');
                    setPeriodFilter('all');
                  }}
                  className="text-sm text-[#194eb2] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
            
            {/* Results count */}
            <div className="text-sm text-slate-500">
              Showing {filteredInvoices.length} of {clientInvoices.length} invoices
            </div>
          </div>
        </div>

        {/* Client Invoices Table */}
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left">Invoice #</th>
                  <th className="px-6 py-3 text-left">Client</th>
                  <th className="px-6 py-3 text-left">Date</th>
                  <th className="px-6 py-3 text-right">Estimated</th>
                  <th className="px-6 py-3 text-right">Actual</th>
                  <th className="px-6 py-3 text-right">Diff</th>
                  <th className="px-6 py-3 text-center">Timesheets</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                      No client invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv) => {
                    const estimated = estimatedBillingMap[inv.id] || 0;
                    const actual = inv.subtotal || 0;
                    
                    return (
                      <tr key={inv.id} className="table-row">
                        <td className="px-6 py-4 font-mono text-sm font-medium text-slate-900">
                          {inv.invoice_number}
                        </td>
                        <td className="px-6 py-4 text-slate-900">
                          {clientsMap[inv.client_id]?.name || '-'}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{formatDate(inv.invoice_date)}</td>
                        <td className="px-6 py-4 text-right font-mono text-sm text-slate-500">
                          {estimated > 0 ? `$${estimated.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-sm font-medium text-slate-900">
                          ${actual.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <DifferenceDisplay estimated={estimated} actual={actual} />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            inv.timesheet_ids?.length > 0 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {inv.timesheet_ids?.length || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <StatusBadge status={inv.status} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                const token = localStorage.getItem('token');
                                window.open(`${API_URL}/api/reports/invoice/${inv.id}/pdf?token=${token}`, '_blank');
                              }}
                              className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewInvoice(inv)}
                              className="p-1.5 text-slate-400 hover:text-[#194eb2] hover:bg-slate-100 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Edit button - admin can edit any invoice */}
                            <button
                              onClick={() => handleEditInvoice(inv)}
                              className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Edit Invoice"
                              data-testid="edit-invoice-btn"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {inv.status !== 'paid' && (
                              <button
                                onClick={() => updateStatus(inv.id, 'paid')}
                                className="text-sm text-emerald-600 hover:underline flex items-center gap-1"
                              >
                                <Check className="w-4 h-4" />
                                Mark Paid
                              </button>
                            )}
                            {/* Delete button - admin can delete any invoice */}
                            <button
                              onClick={() => handleDeleteInvoice(inv)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Invoice"
                              data-testid="delete-invoice-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Client Invoice Modal */}
      <ClientInvoiceModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        onSave={handleSaveClientInvoice}
        clients={clients}
        jobs={jobs}
        resources={resources}
        allClientInvoices={clientInvoices}
        saving={saving}
      />

      {/* Invoice View Modal */}
      <InvoiceViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        invoice={viewingInvoice}
        timesheets={allTimesheets}
        customEntries={allCustomEntries}
        resources={resources}
        jobs={jobs}
        clientsMap={clientsMap}
      />

      {/* Edit Invoice Modal */}
      <EditInvoiceModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingInvoice(null);
        }}
        onSave={handleUpdateInvoice}
        invoice={editingInvoice}
        timesheets={allTimesheets}
        customEntries={allCustomEntries}
        resources={resources}
        jobs={jobs}
        allClientInvoices={clientInvoices}
        saving={saving}
      />
    </DashboardLayout>
  );
};

export default InvoicesPage;
